import React, { useEffect, useState } from 'react';
import './DataAndDocsTab.scss';
import Helpers from '../../../utils/helper';
import filterFactory from '../../../components/react-bootstrap-table-next/react-bootstrap-table2-filter';
import BootstrapTable, {
    TableChangeState,
    TableChangeType,
} from '../../../components/react-bootstrap-table-next/react-bootstrap-table2';
import { Button } from 'reactstrap';
import { useLocation } from 'react-router-dom';
import { LinkedDataRequestProject } from '../../../slices/marketplace/timeline/models/linked-data-request-project.model';
import { CreateDataAndDocsModal } from './CreateDataAndDocsModal/CreateDataAndDocsModal';
import { useStateSelector } from '../../../store/selectors';
import { useDispatch } from 'react-redux';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineActionType } from '../../../slices/marketplace/timeline/models/timeline-action-type.model';
import {
    LinkedDataRequestProjectStatus,
    LinkedDataRequestProjectStatusColors,
    LinkedDataRequestProjectStatusesTransitions,
    LinkedDataRequestProjectStatuses,
} from '../../../slices/marketplace/timeline/models/enums/linked-data-request-project-status.enum';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import createNotification from '../../../utils/createNotification';
import ConfirmationModal from '../../../components/Modal/ConfirmationModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { Tooltip } from 'react-tooltip';
import { EngagementProcessStatus } from '../../../slices/marketplace/timeline/models/enums/engagement-process-status.enum';
import { DataRequestProjectView } from './DataRequestProjectView/DataRequestProjectView';
import { GetTimelineById } from '../../../slices/marketplace/timeline/thunks/get-timeline-by-id.thunk';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';
import {
    formatBusinessDatePt,
    getEventDeadlineDateUtc,
} from '../../../slices/marketplace/timeline/utils/timeline-dates';

interface DataAndDocsTabProps {
    engagementId: number;
    preSelectedProjectId?: string;
    resetPreSelectedProjectId?(): void;
    isReadOnly?: boolean;
}
interface DataAndDocsRow {
    projectName: string;
    dateCreated: Date;
    deadlineDate: Date;
    status: LinkedDataRequestProjectStatus;
    id: string;
    isLate: boolean;
    isBasedOnEvent: boolean;
    isAvailable: boolean;
    [key: string]: string | Date | LinkedDataRequestProjectStatus | boolean;
}
export const DataAndDocsTab = (props: DataAndDocsTabProps) => {
    const location = useLocation();
    const [locState, setLocState] = useState(
        (location.state || {}) as {
            projectId?: number;
            questionnaireId?: number;
        }
    );
    const axios = useStateSelector((state) => state.core.axios);
    const dispatch = useDispatch() as any;
    const party = useCurrentMarketplaceParty(props.engagementId);
    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );

    const [isLoading, setIsLoading] = React.useState(true);
    const [projects, setProjects] = React.useState<DataAndDocsRow[]>([]);

    const [selectedProjectId, setSelectedProjectId] = useState(
        props.preSelectedProjectId
    );

    const defaultSorted: [{ dataField: any; order: any }] = [
        {
            dataField: 'deadlineDate',
            order: 'asc',
        },
    ];
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState<DataAndDocsRow>();
    const [selectedStatus, setSelectedStatus] =
        useState<LinkedDataRequestProjectStatus>();
    const [confirmationText, setConfirmationText] = useState<string>();
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [projectToChangeVisibility, setProjectToChangeVisibility] =
        useState<DataAndDocsRow>();
    const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);
    const [projectToView, setProjectToView] =
        useState<LinkedDataRequestProject>();

    const tableColumns = [
        {
            dataField: 'projectName',
            text: 'Request Name',
            headerClasses: 'project-name-column-header',
            sort: true,
            formatter: (cellContent: string, proj: DataAndDocsRow) => {
                return (
                    <div
                        className="link-button"
                        onClick={() => {
                            const project =
                                currentEngagement.engagementData.dataRequestProjects.find(
                                    (p) => p.id === proj.id
                                );
                            setProjectToView(project);
                        }}>
                        <span className="title-col">
                            {cellContent}

                            <NotificationFlag
                                eventCode={`${NotificationFlagsCategories.MpCode}.${currentEngagement?.id}.${NotificationFlagsCategories.DataAndDocsCode}.${NotificationFlagsCategories.DataRequestsCode}.${proj.id}`}
                            />
                        </span>
                    </div>
                );
            },
        },
        {
            dataField: 'dateCreated',
            text: 'Date Created',
            headerClasses: 'date-created-column-header',
            sort: true,
            formatter: (cellContent: string | Date) => {
                return <div>{new Date(cellContent).toLocaleDateString()}</div>;
            },
        },
        {
            dataField: 'deadlineDate',
            text: 'Deadline Date',
            headerClasses: 'deadline-date-column-header',
            sort: true,
            formatter: (cellContent: string | Date) => {
                return <div>{formatBusinessDatePt(cellContent)}</div>;
            },
        },
        {
            dataField: 'status',
            text: 'Status',
            headerClasses: 'status-column-header',
            sort: true,
            formatter: (
                cellContent: LinkedDataRequestProjectStatus,
                row: DataAndDocsRow
            ) => {
                const isLate =
                    row.isLate &&
                    cellContent === LinkedDataRequestProjectStatus.InProgress;
                return party === TimelineParty.Seller ||
                    party === TimelineParty.Broker ||
                    party === TimelineParty.Observer ? (
                    <div
                        style={{
                            color: LinkedDataRequestProjectStatusColors[
                                cellContent
                            ],
                        }}>
                        {isLate
                            ? 'Late'
                            : LinkedDataRequestProjectStatuses.find(
                                  (s) => s.key === cellContent
                              ).label}
                    </div>
                ) : (
                    <div className="customerStyle">
                        <select
                            className="selectCustomer"
                            disabled={
                                (row.isBasedOnEvent &&
                                    row.status ===
                                        LinkedDataRequestProjectStatus.Upcoming) ||
                                currentEngagement.engagementProcess.status ===
                                    EngagementProcessStatus.Completed
                            }
                            value={row.status}
                            onChange={(event) => {
                                prepareUpdateStatusProcess(
                                    Number(
                                        event.target.value
                                    ) as LinkedDataRequestProjectStatus,
                                    row
                                );
                            }}>
                            {LinkedDataRequestProjectStatuses.filter(
                                (c) =>
                                    Array.from(
                                        LinkedDataRequestProjectStatusesTransitions.get(
                                            row.status
                                        )
                                    ).includes(c.key) || c.key === row.status
                            ).map((item) => (
                                <option
                                    value={item.key}
                                    key={'status ' + item.key}>
                                    {isLate &&
                                    item.key ===
                                        LinkedDataRequestProjectStatus.InProgress
                                        ? 'Late'
                                        : item.label}
                                </option>
                            ))}
                        </select>
                    </div>
                );
            },
        },
        ...(party === TimelineParty.VetValue
            ? [
                  {
                      dataField: 'id',
                      text: 'Actions',
                      headerClasses: 'action-column-header',
                      sort: false,
                      formatter: (
                          cellContent: string,
                          proj: DataAndDocsRow
                      ) => {
                          return (
                              <div>
                                  <FontAwesomeIcon
                                      size="lg"
                                      className="hide-icon"
                                      icon={
                                          proj.isAvailable ? faEye : faEyeSlash
                                      }
                                      id={`proj-${proj.id}`}
                                      data-tooltip-html={
                                          proj.isAvailable
                                              ? 'Make hidden'
                                              : 'Make visible'
                                      }
                                      onClick={() => {
                                          setProjectToChangeVisibility(proj);
                                      }}
                                  />
                                  <Tooltip
                                      place={'top'}
                                      className={'tooltip-content place-top'}
                                      anchorId={`proj-${proj.id}`}
                                      classNameArrow="arrow"
                                      offset={15}
                                  />
                              </div>
                          );
                      },
                  },
              ]
            : []),
    ];

    const prepareUpdateStatusProcess = (
        status: LinkedDataRequestProjectStatus,
        row: DataAndDocsRow
    ) => {
        setSelectedProject(projects.find((p) => p.id === row.id));
        setSelectedStatus(status);
        const ev = currentEngagement.events.find(
            (e) =>
                e.linkedAction?.actionType ===
                    TimelineActionType.SellerDataAndDocumentRequest &&
                e.linkedAction?.referenceItemId === row.id
        );
        const lbl = LinkedDataRequestProjectStatuses.find(
            (s) => s.key === status
        ).label;
        if (!ev) {
            setConfirmationText(
                `Are you sure you want to change the Project status to ${lbl}?`
            );
        } else if (ev.status !== TimelineEventStatus.Completed) {
            setConfirmationText(
                `Are you sure you want to change the Project status to ${lbl}? This will also update the status of the event "${ev.name}" to ${lbl}`
            );
        } else {
            setConfirmationText(
                `Are you sure you want to change the Project status to ${lbl}? This will not update the status of the event "${ev.name}"`
            );
        }
    };

    const launchUpdateStatusProcess = (
        status: LinkedDataRequestProjectStatus,
        row: DataAndDocsRow
    ) => {
        setIsUpdatingStatus(true);
        axios
            .put(
                `/api/Marketplace/Engagements/${props.engagementId}/DataRequests/${row.id}/status`,
                status,
                {
                    headers: { 'Content-type': 'application/json' },
                }
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An unexpected error occurred while updating the status.',
                        'error'
                    );
                    return;
                }
                createNotification('Status updated successfully.', 'success');
                fetchProjects();
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while updating the status.',
                    'error'
                );
            })
            .finally(() => {
                setIsUpdatingStatus(false);
                setSelectedProject(undefined);
                setSelectedStatus(undefined);
                setConfirmationText(undefined);
            });
    };

    const launchVisibilityUpdate = () => {
        setIsUpdatingVisibility(true);
        axios
            .put(
                `/api/Marketplace/Engagements/${props.engagementId}/DataRequests/${projectToChangeVisibility.id}/availability`,
                !projectToChangeVisibility.isAvailable,
                {
                    headers: { 'Content-type': 'application/json' },
                }
            )
            .then((response) => {
                if (response.status !== 200) {
                    createNotification(
                        'An unexpected error occurred while updating the visibility.',
                        'error'
                    );
                    return;
                }
                createNotification(
                    `Project "${projectToChangeVisibility.projectName}" visibility updated successfully.`,
                    'success'
                );
                fetchProjects();
            })
            .catch(() => {
                createNotification(
                    'An unexpected error occurred while updating the visibility.',
                    'error'
                );
            })
            .finally(() => {
                setProjectToChangeVisibility(undefined);
                setIsUpdatingVisibility(false);
            });
    };

    const handleTableChange = (
        type: TableChangeType,
        newState: TableChangeState<any>
    ) => {
        if (type === 'sort') {
            const { sortField, sortOrder } = newState;
            if (sortField && sortOrder) {
                const sortedProjects = [...projects].sort((a, b) => {
                    let aValue = a[sortField];
                    let bValue = b[sortField];

                    if (aValue instanceof Date && bValue instanceof Date) {
                        return sortOrder === 'asc'
                            ? aValue.getTime() - bValue.getTime()
                            : bValue.getTime() - aValue.getTime();
                    }

                    if (
                        typeof aValue === 'string' &&
                        typeof bValue === 'string'
                    ) {
                        return sortOrder === 'asc'
                            ? aValue.localeCompare(bValue)
                            : bValue.localeCompare(aValue);
                    }
                    if (
                        typeof aValue === 'number' &&
                        typeof bValue === 'number'
                    ) {
                        return sortOrder === 'asc'
                            ? aValue - bValue
                            : bValue - aValue;
                    }
                    return 0;
                });
                setProjects(sortedProjects);
            }
        }
    };

    useEffect(() => {
        resetHighlighting();
    }, []);

    const resetHighlighting = () => {
        props.resetPreSelectedProjectId();

        setTimeout(() => {
            setSelectedProjectId(null);
        }, 2000);
    };

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = () => {
        setIsLoading(true);
        dispatch(
            GetTimelineById({ id: props.engagementId, isTemplate: false })
        ).then(() => {
            axios
                .get(
                    `/api/Marketplace/Engagements/${props.engagementId}/DataRequests`
                )
                .then((response) => {
                    if (response.status !== 200) {
                        createNotification(
                            'An unexpected error occurred while fetching data & docs projects.',
                            'error'
                        );
                        return;
                    }
                    const mappedProjects = filterAndMapProjects(
                        response.data as LinkedDataRequestProject[]
                    );
                    setProjects(mappedProjects);

                    if (locState.projectId && locState.questionnaireId) {
                        const project =
                            currentEngagement.engagementData.dataRequestProjects.find(
                                (p) =>
                                    p.dataRequestProjectId ===
                                    locState.projectId
                            );
                        setProjectToView(project);
                    }
                })
                .catch(() => {
                    createNotification(
                        'An unexpected error occurred while fetching data & docs projects.',
                        'error'
                    );
                })
                .finally(() => {
                    setIsLoading(false);
                });
        });
    };

    const filterAndMapProjects = (
        projects: LinkedDataRequestProject[]
    ): DataAndDocsRow[] => {
        return projects
            .filter(
                (p) =>
                    party === TimelineParty.VetValue ||
                    (p.status !== LinkedDataRequestProjectStatus.Upcoming &&
                        p.isAvailable)
            )
            .map((project) => {
                const ev = currentEngagement.events.find(
                    (e) =>
                        e.linkedAction?.actionType ===
                            TimelineActionType.SellerDataAndDocumentRequest &&
                        e.linkedAction?.referenceItemId === project.id
                );
                const deadlineEvent = ev ?? currentEngagement.events[currentEngagement.events.length - 1];
                const deadlineDate = getEventDeadlineDateUtc(
                    currentEngagement.engagementProcess.startDate,
                    deadlineEvent
                );
                return {
                    projectName: project.projectName,
                    dateCreated: new Date(project.dateCreated),
                    deadlineDate: deadlineDate,
                    status: project.status,
                    id: project.id,
                    isLate: ev
                        ? ev.status === TimelineEventStatus.Late
                        : currentEngagement.events[
                              currentEngagement.events.length - 1
                          ]?.status === TimelineEventStatus.Late,
                    isBasedOnEvent: !!ev,
                    isAvailable: project.isAvailable,
                    linkedProjectId: project.dataRequestProjectId,
                };
            });
    };

    return (
        <div className="data-and-docs-tab">
            {!projectToView ? (
                <div className="body_inner_content">
                    <div className="obpbq_row valuation_section">
                        <div className="sampledetail">
                            <div className="mb-2 d-flex justify-content-between align-items-center">
                                <strong>Practice Data & Docs</strong>
                                {party === TimelineParty.VetValue ? (
                                    <Button
                                        color="primary"
                                        onClick={() =>
                                            setIsCreateModalOpen(true)
                                        }
                                        disabled={
                                            currentEngagement.engagementProcess
                                                .status ===
                                            EngagementProcessStatus.Completed
                                        }>
                                        Add practice Data & Docs
                                    </Button>
                                ) : (
                                    []
                                )}
                            </div>
                            <div
                                role="tabpanel"
                                id="panel-0"
                                aria-labelledby="tab-0"
                                aria-hidden="false">
                                <div className="card-body">
                                    {isLoading ? (
                                        Helpers.renderTableLoader()
                                    ) : (
                                        <BootstrapTable
                                            striped
                                            bordered
                                            bootstrap4
                                            remote
                                            keyField="id"
                                            data={projects}
                                            columns={tableColumns}
                                            defaultSorted={defaultSorted}
                                            filter={filterFactory()}
                                            onTableChange={handleTableChange}
                                            noDataIndication="No Data & Docs Projects available"
                                            rowStyle={{ height: '44px' }}
                                            rowClasses={(row, index) => {
                                                if (
                                                    selectedProjectId === row.id
                                                ) {
                                                    return index % 2
                                                        ? 'highlighted'
                                                        : 'highlighted-dark';
                                                }

                                                return '';
                                            }}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <DataRequestProjectView
                    project={projectToView}
                    isReadOnly={props.isReadOnly}
                    onCancel={() => {
                        if (location.state) {
                            location.state = {
                                ...location.state,
                                projectId: undefined,
                                questionnaireId: undefined,
                            };
                            setLocState({
                                projectId: undefined,
                                questionnaireId: undefined,
                            });
                        }
                        setProjectToView(undefined);
                    }}
                    party={party}
                    questionnaireId={locState.questionnaireId}
                    engagementId={props.engagementId}
                />
            )}
            {isCreateModalOpen ? (
                <CreateDataAndDocsModal
                    engagementId={props.engagementId}
                    isVisible={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    onSubmit={() => {
                        fetchProjects();
                        setIsCreateModalOpen(false);
                    }}
                />
            ) : (
                []
            )}
            {selectedProject && selectedStatus ? (
                <ConfirmationModal
                    isVisible={selectedProject && selectedStatus}
                    setIsVisible={() => {
                        setSelectedProject(undefined);
                        setSelectedStatus(undefined);
                        setConfirmationText(undefined);
                    }}
                    header="Are you sure?"
                    confirmationText={confirmationText}
                    onConfirm={() =>
                        launchUpdateStatusProcess(
                            selectedStatus,
                            selectedProject
                        )
                    }
                    params={undefined}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isUpdatingStatus}></ConfirmationModal>
            ) : (
                []
            )}
            {projectToChangeVisibility ? (
                <ConfirmationModal
                    isVisible={!!projectToChangeVisibility}
                    setIsVisible={() => setProjectToChangeVisibility(undefined)}
                    header="Are you sure?"
                    confirmationText={`Are you sure you want to ${
                        projectToChangeVisibility.isAvailable
                            ? 'hide'
                            : 'make visible'
                    } the project "${
                        projectToChangeVisibility.projectName
                    }" for Seller?`}
                    onConfirm={launchVisibilityUpdate}
                    params={undefined}
                    nextButtonText="Confirm"
                    cancelButtonText="Cancel"
                    closeOnConfirm={false}
                    isLoading={isUpdatingVisibility}></ConfirmationModal>
            ) : (
                []
            )}
        </div>
    );
};
